import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ModeEditOutline from '@mui/icons-material/ModeEditOutline';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DataGridPro } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { deleteStockDelta, getScenario } from '../../../shared/api/scenarioApi';
import CardContainer from '../../../shared/components/CardContainer';
import CardHeader from '../../../shared/components/CardHeader';
import ExpSnackbarAlerts from '../../../shared/components/display/ExpSnackbarAlerts';
import { useScenario } from '../../../shared/contexts/ScenarioContext';
import { useUserPreferences } from '../../../shared/contexts/UserPreferencesContext';
import useColumnPreferences from '../../../shared/hooks/useColumnPreferences';
import useScenarioMode from '../../../shared/hooks/useScenarioMode';
import useUserRoles from '../../../shared/hooks/useUserRoles';
import { cwDataGridSx } from '../../../shared/utils/calendarWeekStyling';
import { formatNumberDE } from '../../../shared/utils/formatNumber';
import { scenarioDataGridRowSx } from '../../../shared/utils/scenarioHighlight';
import { getVoyageWithShipments } from '../../vessel/services/VoyageRepository';
import { useStockData, useReportingCoordination } from '../../vessel/store';
import TotalBlockedCarChart from '../../vessel/views/sales/components/charts/TotalBlockedCarChart';
import ColumnPrefsToolbar from '../../vessel/views/sales/components/shared/ColumnPrefsToolbar';
import GroupEditModal from '../../vessel/views/sales/modals/VoyageGroupEditModal';
import StockModal from './components/StockModal';
import StockQuickEditModal from './components/StockQuickEditModal';
import StockRecalculationModal from './components/StockRecalculationModal';
import StockVoyageSidePanel from './components/StockVoyageDetailPanel';

dayjs.extend(isoWeek);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns a formatted date string or empty if invalid */
const createDateFormatter = (format) => (params) => {
  const value = params?.value ?? params;
  if (value == null || value === '') return '';
  const d = dayjs(value);
  return d.isValid() ? d.format(format) : '';
};

// ---------------------------------------------------------------------------
// DataGrid sx overrides — disable hover, selection styles + CW band colors
// ---------------------------------------------------------------------------

const dataGridSx = {
  // Disable hover highlight on non-selected rows, add pointer cursor
  '& .MuiDataGrid-row': {
    cursor: 'pointer',
    '&:hover, &.Mui-hovered': { backgroundColor: 'transparent !important' },
  },
  '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
    backgroundColor: 'transparent !important',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none !important',
  },

  // Scenario delta row highlights (centralised in scenarioHighlight.ts)
  ...scenarioDataGridRowSx,

  // Highlighted "today" row
  '& .today-highlight': {
    outline: (t) => `2px solid ${t.palette.primary.main}`,
    outlineOffset: -2,
  },

  // Selected row (clicked to open side panel) — preserve on hover
  '& .selected-row, & .selected-row:hover, & .selected-row.Mui-hovered': {
    backgroundColor: (t) =>
      t.palette.mode === 'dark'
        ? 'rgba(0, 120, 212, 0.16) !important'
        : 'rgba(0, 120, 212, 0.08) !important',
  },
  '& .selected-row:hover .MuiDataGrid-cell': {
    backgroundColor: 'transparent !important',
  },

  // Calendar-week band colors (shared) + zero cell padding on bottleneck for full-tile background
  ...cwDataGridSx,
  "& .MuiDataGrid-cell[data-field='bottleneck']": { padding: '0 !important' },
};

// ---------------------------------------------------------------------------
// Render helpers (stateless — safe at module level)
// ---------------------------------------------------------------------------

/** Renders "—" for null/undefined, otherwise the raw value — vertically centered */
const renderNullable = (params) => (
  <Box display='flex' alignItems='center' height='100%'>
    <Typography variant='body2'>
      {params.formattedValue != null && params.formattedValue !== ''
        ? params.formattedValue
        : '—'}
    </Typography>
  </Box>
);

/** Color bottleneck tile: negative = red bg, positive = green bg, zero = default */
const renderBottleneck = (params) => {
  const value = params.value;
  if (value == null)
    return (
      <Box
        display='flex'
        alignItems='center'
        width='100%'
        height='100%'
        sx={{ padding: '0 10px' }}
      >
        <Typography variant='body2'>—</Typography>
      </Box>
    );
  const bg =
    value < 0
      ? (t) =>
          t.palette.mode === 'dark'
            ? 'rgba(211, 47, 47, 0.35)'
            : 'rgba(211, 47, 47, 0.22)'
      : value > 0
        ? (t) =>
            t.palette.mode === 'dark'
              ? 'rgba(46, 125, 50, 0.35)'
              : 'rgba(46, 125, 50, 0.22)'
        : null;
  const color =
    value < 0 ? 'error.main' : value > 0 ? 'success.main' : 'text.primary';
  return (
    <Box
      display='flex'
      alignItems='center'
      width='100%'
      height='100%'
      sx={{ padding: '0 10px', ...(bg ? { backgroundColor: bg } : {}) }}
    >
      <Typography variant='body2' fontWeight='medium' sx={{ color }}>
        {params.formattedValue ?? value}
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Builds hold→{name, shortName, totalVolume} mapping from the lightweight
 * BlockedSummaryDTO shape returned by /aggregatedStock/details.
 * Shape: { holdId, holdName, holdShortName, volume }
 */
const buildBlockedMapping = (blockeds = []) =>
  blockeds.reduce((acc, b) => {
    const key = b.holdName;
    if (!acc[key]) {
      acc[key] = {
        name: b.holdName,
        shortName: b.holdShortName,
        totalVolume: 0,
      };
    }
    acc[key].totalVolume += b.volume || 0;
    return acc;
  }, {});

const StockTable = ({
  data = [],
  isStockLoading = false,
  onScenarioDeltaChange,
  onEditClicked,
}) => {
  const { isScenarioActive, isScenarioViewOnly } = useScenarioMode();
  const { activeScenario } = useScenario();
  // stockDeltaKeyMap: date → { changeType, deltaIds }
  const [stockDeltaKeyMap, setStockDeltaKeyMap] = useState({});
  const [deltaRefreshTick, setDeltaRefreshTick] = useState(0);
  const [undoingDateKey, setUndoingDateKey] = useState(null);

  useEffect(() => {
    if (!isScenarioActive || !activeScenario) {
      setStockDeltaKeyMap({});
      return;
    }
    getScenario(activeScenario.id)
      .then((dto) => {
        const keyMap = {};
        (dto.stockDeltas ?? []).forEach((d) => {
          const dj = d.deltaJson;
          if (!dj) return;
          const dateKey = dj.date ? String(dj.date).split('T')[0] : null;
          if (!dateKey) return;
          // Date-only key: summary rows are aggregated and may not carry portOperatorId
          if (!keyMap[dateKey]) {
            keyMap[dateKey] = { changeType: d.changeType, deltaIds: [] };
          }
          keyMap[dateKey].deltaIds.push(d.id);
        });
        setStockDeltaKeyMap(keyMap);
      })
      .catch(() => {
        setStockDeltaKeyMap({});
      });
  }, [isScenarioActive, activeScenario, deltaRefreshTick, data]);

  // Normalize: support both plain array (legacy) and Spring Page object ({content: []})
  const rows = useMemo(
    () =>
      Array.isArray(data)
        ? data
        : Array.isArray(data?.content)
          ? data.content
          : [],
    [data]
  );

  const { fetchStockDetails, baseStockData } = useStockData();
  const { fetchAllData } = useReportingCoordination();

  // Fetch full voyage data on demand when the edit button is clicked in the
  // side panel.  This replaces the previous voyageIdDataMap approach which
  // relied on the paginated `voyages` prop — that data only refreshes when
  // the Vessel Table tab is active, causing stale/missing lookups after
  // scenario edits or tab switches.
  const handleVoyageEdit = useCallback(
    async (voyageId) => {
      try {
        const params = activeScenario ? { scenarioId: activeScenario.id } : {};
        const voyageData = await getVoyageWithShipments(voyageId, params);
        onEditClicked(voyageData);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[StockTable] Failed to fetch voyage for edit:', error);
      }
    },
    [onEditClicked, activeScenario]
  );

  // Compare fields used for detecting cell-level changes
  const COMPARE_FIELDS = useMemo(
    () => [
      'currentStock',
      'shippedSum',
      'readyForShipment',
      'bottleneck',
      'differenceInflow',
      'openingInventory',
      'totalBlockedCars',
    ],
    []
  );

  // Map<dateKey, Set<fieldName>> — tracks which cells changed between base and scenario.
  // Covers both manual stock deltas and auto-calculated voyage side effects.
  const changedCellsMap = useMemo(() => {
    if (!isScenarioActive || !baseStockData) return new Map();

    const baseRows = Array.isArray(baseStockData)
      ? baseStockData
      : Array.isArray(baseStockData?.content)
        ? baseStockData.content
        : [];

    if (baseRows.length === 0) return new Map();

    const baseByDate = {};
    baseRows.forEach((row) => {
      const dateKey = row.date ? String(row.date).split('T')[0] : null;
      if (dateKey) baseByDate[dateKey] = row;
    });

    const scenarioRows = Array.isArray(data)
      ? data
      : Array.isArray(data?.content)
        ? data.content
        : [];

    const result = new Map();

    scenarioRows.forEach((row) => {
      const dateKey = row.date ? String(row.date).split('T')[0] : null;
      if (!dateKey) return;

      const baseRow = baseByDate[dateKey];
      if (!baseRow) {
        // Row exists in scenario but not in base — all fields are "changed"
        result.set(dateKey, new Set(COMPARE_FIELDS));
        return;
      }

      const changedFields = new Set();
      for (const field of COMPARE_FIELDS) {
        const scenarioVal = row[field] ?? 0;
        const baseVal = baseRow[field] ?? 0;
        if (scenarioVal !== baseVal) {
          changedFields.add(field);
        }
      }
      if (changedFields.size > 0) {
        result.set(dateKey, changedFields);
      }
    });

    return result;
  }, [isScenarioActive, baseStockData, data, COMPARE_FIELDS]);

  // Derive the row-level side-effect set: dates in changedCellsMap that have
  // NO direct stock delta (those get the diagonal stripe row highlight).
  const voyageSideEffectDates = useMemo(() => {
    const dates = new Set();
    changedCellsMap.forEach((_fields, dateKey) => {
      if (!stockDeltaKeyMap[dateKey]) dates.add(dateKey);
    });
    return dates;
  }, [changedCellsMap, stockDeltaKeyMap]);

  // Only the earliest changed date gets a row-level background highlight.
  const earliestChangedDate = useMemo(() => {
    if (changedCellsMap.size === 0) return null;
    let min = null;
    for (const dateKey of changedCellsMap.keys()) {
      if (min === null || dateKey < min) min = dateKey;
    }
    return min;
  }, [changedCellsMap]);
  const { preferences } = useUserPreferences();
  const { isShippingCompanyUser, isAdmin } = useUserRoles();
  const colPrefs = useColumnPreferences({
    hiddenKey: 'hiddenStockTableColumns',
    orderKey: 'stockTableColumnOrder',
    widthsKey: 'stockTableColumnWidths',
    pinnedKey: 'stockTablePinnedColumns',
    defaultPinned: { left: ['date'] },
  });
  const { apiRef, snackbar } = colPrefs;
  const dateFormat = preferences.dateFormat || 'DD.MM.YYYY';
  const canEditStock = !isShippingCompanyUser;

  const [detailData, setDetailData] = useState({});
  const [detailsFetched, setDetailsFetched] = useState(false);
  const pendingDetailFetch = useRef(false);

  const [undoSnackbar, setUndoSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleUndoStockDelta = useCallback(
    async (dateKey) => {
      const scenarioId = activeScenario?.id;
      const delta = stockDeltaKeyMap[dateKey];
      if (!scenarioId || !delta || undoingDateKey) return;

      setUndoingDateKey(dateKey);
      try {
        await Promise.all(
          delta.deltaIds.map((id) => deleteStockDelta(scenarioId, id))
        );
        setUndoSnackbar({
          open: true,
          message: 'Stock scenario change undone',
          severity: 'success',
        });
        fetchAllData();
        setDeltaRefreshTick((t) => t + 1);
        onScenarioDeltaChange?.();
      } catch (error) {
        console.error('[StockTable] Undo stock delta failed:', error);
        setUndoSnackbar({
          open: true,
          message: error?.message || 'Error undoing stock scenario change',
          severity: 'error',
        });
      } finally {
        setUndoingDateKey(null);
      }
    },
    [
      activeScenario,
      stockDeltaKeyMap,
      undoingDateKey,
      fetchAllData,
      onScenarioDeltaChange,
    ]
  );

  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [isRecalculateModalOpen, setIsRecalculateModalOpen] = useState(false);
  const [isVoyageModalOpen, setIsVoyageModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const toggleFullScreen = useCallback(
    () => setIsFullScreen((prev) => !prev),
    []
  );

  const dateFormatter = useMemo(
    () => createDateFormatter(dateFormat),
    [dateFormat]
  );

  // For each row: if actualInflow is null, find the nearest earlier date where it is not null
  const blockedCarSourceDateMap = useMemo(() => {
    const sorted = [...rows].sort((a, b) => dayjs(b.date).diff(dayjs(a.date)));
    const result = {};
    for (let i = 0; i < sorted.length; i++) {
      const row = sorted[i];
      if (row.actualInflow != null) {
        result[row.date] = null;
      } else {
        const found = sorted.slice(i + 1).find((r) => r.actualInflow != null);
        result[row.date] = found ? dayjs(found.date).format(dateFormat) : null;
      }
    }
    return result;
  }, [rows, dateFormat]);

  // Lazy loading cache for weekly and monthly aggregations
  const aggregationCache = useRef({});

  // Reset cache when data changes
  useEffect(() => {
    aggregationCache.current = {};
  }, [data]);

  // Lazy calculation function for weekly/monthly totals
  const getAggregations = useCallback(
    (weekKey, monthKey) => {
      const cacheKey = `${weekKey}|${monthKey}`;

      if (aggregationCache.current[cacheKey]) {
        return aggregationCache.current[cacheKey];
      }

      let weeklyTotal = 0;
      let monthlyTotal = 0;

      rows.forEach((row) => {
        if (row.date && row.differenceInflow != null) {
          const date = dayjs(row.date);
          if (date.isValid()) {
            const rowWeekKey = `${date.isoWeekYear()}-W${date.isoWeek()}`;
            const rowMonthKey = date.format('YYYY-MM');

            if (rowWeekKey === weekKey) {
              weeklyTotal += row.differenceInflow;
            }
            if (rowMonthKey === monthKey) {
              monthlyTotal += row.differenceInflow;
            }
          }
        }
      });

      const result = { weeklyTotal, monthlyTotal };
      aggregationCache.current[cacheKey] = result;
      return result;
    },
    [rows]
  );

  const canQuickEditStock =
    canEditStock && isScenarioActive && !isScenarioViewOnly;

  // Date (string) currently open in the Quick Edit modal, or null when closed
  const [quickEditDate, setQuickEditDate] = useState(null);
  const openQuickEdit = useCallback((date) => setQuickEditDate(date), []);
  const closeQuickEdit = useCallback(() => setQuickEditDate(null), []);

  /**
   * Wraps a numeric metric cell so it opens the Quick Edit modal on click
   * (gated by canQuickEditStock) with a clear hover affordance: pointer
   * cursor, subtle hover tint, and a fade-in edit icon.
   */
  const renderQuickEditCell = useCallback(
    () => (params) => {
      const content = (
        <Typography variant='body2'>
          {params.formattedValue != null && params.formattedValue !== ''
            ? params.formattedValue
            : '—'}
        </Typography>
      );
      if (!canQuickEditStock) {
        return (
          <Box display='flex' alignItems='center' height='100%'>
            {content}
          </Box>
        );
      }
      return (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            openQuickEdit(params.row.date);
          }}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
            px: 1,
            cursor: 'pointer',
            borderRadius: 0.5,
            '&:hover': { backgroundColor: 'action.hover' },
            '&:hover .quick-edit-hover-icon': { opacity: 1 },
          }}
        >
          {content}
          <ModeEditOutline
            className='quick-edit-hover-icon'
            fontSize='inherit'
            sx={{
              opacity: 0,
              transition: 'opacity 0.15s',
              color: 'action.active',
            }}
          />
        </Box>
      );
    },
    [canQuickEditStock, openQuickEdit]
  );

  const stockColumnsDefinitions = useMemo(
    () => [
      {
        field: 'date',
        headerName: 'Date',
        width: 150,
        valueFormatter: dateFormatter,
      },
      {
        field: 'calendarWeek',
        headerName: 'CW',
        description: 'Calendar Week',
        width: 80,
        valueGetter: (_value, row) => {
          const date = row.date;
          if (date == null) return '';
          const d = dayjs(date);
          return d.isValid() ? d.isoWeek() : '';
        },
      },
      {
        field: 'currentStock',
        headerName: 'Current Stock',
        width: 150,
        description:
          'Today: actual from port operator. Future: prev_day_stock + forecast_inflow (projected up to 16 weeks)',
        valueFormatter: formatNumberDE,
        renderCell: renderQuickEditCell(),
      },
      {
        field: 'forecastInflow',
        headerName: 'Forecast Inflow',
        width: 150,
        description: 'Planned vehicle arrivals (from Databricks forecast)',
        valueFormatter: formatNumberDE,
        renderCell: renderQuickEditCell(),
      },
      {
        field: 'actualInflow',
        headerName: 'Actual Inflow',
        width: 150,
        description: 'Confirmed arrivals from port operator feed',
        valueFormatter: formatNumberDE,
        renderCell: renderQuickEditCell(),
      },
      {
        field: 'differenceInflow',
        headerName: 'Difference Inflow',
        width: 160,
        description:
          'forecast_inflow − (actual_inflow ?? 0)\nPositive = fewer arrivals than planned',
        valueFormatter: formatNumberDE,
        renderCell: (params) => {
          const displayValue =
            params.value != null
              ? (params.formattedValue ?? params.value)
              : '—';
          const showTooltip = params.value != null && params.value !== 0;

          let tooltipContent = '';
          if (showTooltip && params.row.date) {
            const date = dayjs(params.row.date);
            if (date.isValid()) {
              const weekKey = `${date.isoWeekYear()}-W${date.isoWeek()}`;
              const monthKey = date.format('YYYY-MM');

              // Lazy load aggregations only when tooltip is shown
              const { weeklyTotal, monthlyTotal } = getAggregations(
                weekKey,
                monthKey
              );

              tooltipContent = (
                <Box>
                  <div>Weekly total (sum): {formatNumberDE(weeklyTotal)}</div>
                  <div>Monthly total (sum): {formatNumberDE(monthlyTotal)}</div>
                </Box>
              );
            }
          }

          return (
            <Box display='flex' alignItems='center' height='100%'>
              {showTooltip ? (
                <Tooltip title={tooltipContent} arrow placement='top'>
                  <Typography variant='body2' sx={{ cursor: 'help' }}>
                    {displayValue}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography variant='body2'>{displayValue}</Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: 'openingInventory',
        headerName: 'Opening Inventory',
        width: 160,
        description:
          'current_stock − (actual_inflow ?? forecast_inflow)\nUses actual inflow when available; forecast otherwise',
        valueFormatter: formatNumberDE,
        renderCell: renderNullable,
      },
      {
        field: 'totalBlockedCars',
        headerName: 'Total Blocked Cars',
        width: 250,
        valueFormatter: formatNumberDE,
        renderCell: (params) => {
          const blockeds = params.row.blockeds;

          let content;
          // Lazy: detail not loaded yet — show the scalar count
          if (blockeds == null) {
            content =
              params.value != null ? (
                <Typography variant='body2'>
                  {params.formattedValue ?? params.value}
                </Typography>
              ) : (
                <Skeleton variant='rounded' width={200} height={40} />
              );
          } else if (blockeds.length === 0) {
            // Detail loaded, nothing blocked — scalar/dash
            content = (
              <Typography variant='body2'>
                {params.value != null
                  ? (params.formattedValue ?? params.value)
                  : '\u2014'}
              </Typography>
            );
          } else {
            // Detail loaded — show breakdown chart
            const mapping = buildBlockedMapping(blockeds);
            const sourceDate = blockedCarSourceDateMap[params.row.date] ?? null;
            content = (
              <TotalBlockedCarChart
                holdblockedMapping={mapping}
                sourceDate={sourceDate}
              />
            );
          }

          if (!canQuickEditStock) {
            return (
              <Box
                width='100%'
                height='100%'
                display='flex'
                alignItems='center'
              >
                {content}
              </Box>
            );
          }
          return (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                openQuickEdit(params.row.date);
              }}
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0.5,
                px: 1,
                cursor: 'pointer',
                borderRadius: 0.5,
                '&:hover': { backgroundColor: 'action.hover' },
                '&:hover .quick-edit-hover-icon': { opacity: 1 },
              }}
            >
              {content}
              <ModeEditOutline
                className='quick-edit-hover-icon'
                fontSize='inherit'
                sx={{
                  opacity: 0,
                  transition: 'opacity 0.15s',
                  color: 'action.active',
                }}
              />
            </Box>
          );
        },
      },
      {
        field: 'readyForShipment',
        headerName: 'Ready For Shipment',
        width: 170,
        description:
          'current_stock[d−2] − total_volume[d−1] − total_blocked_cars[d−1]\nUses prior-day snapshots; carries forward if missing',
        valueFormatter: formatNumberDE,
        renderCell: (params) => (
          <Box display='flex' alignItems='center' height='100%'>
            <Tooltip
              title='current_stock[date−2] − total_volume[date−1] − total_blocked_cars[date−1]'
              arrow
              placement='top'
            >
              <Typography variant='body2' sx={{ cursor: 'help' }}>
                {params.value != null
                  ? (params.formattedValue ?? params.value)
                  : '—'}
              </Typography>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: 'shippedSum',
        headerName: 'Total Volume',
        width: 140,
        description:
          'Σ chosenValue per shipment\nchosenValue = first non-null > 0 of: effective_shipped → final_agreement → adjusted_po_request',
        valueFormatter: formatNumberDE,
        renderCell: renderNullable,
      },
      {
        field: 'bottleneck',
        headerName: 'Bottleneck',
        width: 140,
        description:
          'ready_for_shipment − total_volume\nPositive = surplus capacity, Negative = overbooked',
        valueFormatter: formatNumberDE,
        renderCell: renderBottleneck,
      },
      {
        field: 'voyages',
        headerName: 'Voyage & Shipments',
        width: 320,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const voyages = params.value;
          // Lazy: detail not loaded yet
          if (voyages == null) {
            return (
              <Box display='flex' alignItems='center' height='100%' gap={1}>
                <Skeleton variant='text' width={120} />
                <Skeleton variant='text' width={40} />
              </Box>
            );
          }
          if (voyages.length === 0) {
            return (
              <Box display='flex' alignItems='center' height='100%'>
                <Typography variant='body2' color='text.secondary'>
                  —
                </Typography>
              </Box>
            );
          }

          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                overflowY: 'auto',
                cursor: 'pointer',
                py: 0.5,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRow(params.row);
              }}
            >
              {voyages.map((v) => (
                <Box
                  key={v.voyageId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    py: 0.25,
                    px: 0.25,
                    minWidth: 0,
                    '&:not(:last-child)': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <Typography
                    variant='caption'
                    noWrap
                    sx={{ fontSize: '0.7rem', flex: '0 1 auto', minWidth: 0 }}
                  >
                    {v.shipName ?? '—'}
                  </Typography>
                  <Typography
                    variant='caption'
                    noWrap
                    color='text.secondary'
                    sx={{ fontSize: '0.7rem', flex: '1 1 auto', minWidth: 0 }}
                  >
                    {v.shipCompanyName ?? '—'}
                  </Typography>
                  <Typography
                    variant='caption'
                    noWrap
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      minWidth: 28,
                      textAlign: 'right',
                      flexShrink: 0,
                    }}
                  >
                    {formatNumberDE(v.totalShipped ?? 0)}
                  </Typography>
                  <Tooltip
                    title='Shipment chosenValue = first non-null > 0 of: effective_shipped → final_agreement → adjusted_po_request'
                    arrow
                    placement='top'
                  >
                    <InfoOutlinedIcon
                      fontSize='inherit'
                      sx={{
                        fontSize: '0.85rem',
                        color: 'text.secondary',
                        cursor: 'help',
                        flexShrink: 0,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Tooltip>
                </Box>
              ))}
            </Box>
          );
        },
      },
      ...(canQuickEditStock
        ? [
            {
              field: 'actions',
              headerName: 'Actions',
              width: 110,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              renderCell: (params) => {
                const dateKey = params.row.date
                  ? String(params.row.date).split('T')[0]
                  : null;
                const delta = dateKey ? stockDeltaKeyMap[dateKey] : undefined;
                const isUndoing = undoingDateKey === dateKey;
                return (
                  <Box
                    display='flex'
                    alignItems='center'
                    height='100%'
                    onClick={(e) => e.stopPropagation()}
                  >
                    {canQuickEditStock && (
                      <Tooltip title='Quick Edit Stock'>
                        <IconButton
                          size='small'
                          onClick={() => openQuickEdit(params.row.date)}
                        >
                          <ModeEditOutline fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                    {delta && (
                      <Tooltip title='Undo Scenario Change'>
                        <span>
                          <IconButton
                            color='warning'
                            size='small'
                            disabled={isUndoing || !!undoingDateKey}
                            onClick={() => handleUndoStockDelta(dateKey)}
                          >
                            {isUndoing ? (
                              <CircularProgress size={18} color='inherit' />
                            ) : (
                              <ReplayOutlinedIcon fontSize='small' />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                );
              },
            },
          ]
        : []),
      { field: 'marketCountryCode', headerName: 'Market Country', width: 150 },
      {
        field: 'destinationPortId',
        headerName: 'Destination Port ID',
        width: 150,
      },
      {
        field: 'portOperatorId',
        headerName: 'Port Operator ID',
        width: 150,
      },
      {
        field: 'departurePortId',
        headerName: 'Departure Port ID',
        width: 150,
      },
    ],
    [
      dateFormatter,
      blockedCarSourceDateMap,
      getAggregations,
      stockDeltaKeyMap,
      undoingDateKey,
      handleUndoStockDelta,
      renderQuickEditCell,
      canQuickEditStock,
      openQuickEdit,
    ]
  );

  // Derive composite row IDs and merge lazy-loaded detail data
  const rowsWithId = useMemo(
    () =>
      rows.map((row) => {
        const compositeId = `${row.date ?? 'no-date'}_${row.marketCountryCode ?? 'no-market'}_${row.portOperatorId ?? 'no-operator'}_${row.departurePortId ?? 'no-dept'}_${row.destinationPortId ?? 'no-dest'}`;
        const dateKey = row.date ? row.date.split('T')[0] : null;
        const detail = dateKey ? detailData[dateKey] : null;

        return {
          ...row,
          id: compositeId,
          blockeds: detail?.blockeds ?? (detailsFetched ? [] : null),
          voyages: detail?.voyages ?? (detailsFetched ? [] : null),
        };
      }),
    [rows, detailData, detailsFetched]
  );

  // Sync selectedRow with fresh data after refetch (e.g. after Quick Edit save)
  useEffect(() => {
    if (!selectedRow) return;
    const updated = rowsWithId.find((r) => r.id === selectedRow.id);
    if (updated) {
      setSelectedRow(updated);
    } else {
      setSelectedRow(null);
    }
  }, [rowsWithId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch details for the full date range when summary data arrives
  useEffect(() => {
    if (isStockLoading) {
      // Reset when a new fetch starts (filters changed)
      setDetailData({});
      setDetailsFetched(false);
      pendingDetailFetch.current = false;
      return;
    }
    if (rows.length === 0 || pendingDetailFetch.current) return;

    pendingDetailFetch.current = true;
    fetchStockDetails().then((cache) => {
      setDetailData({ ...cache });
      setDetailsFetched(true);
      pendingDetailFetch.current = false;
    });
  }, [rows, isStockLoading, fetchStockDetails]);

  /** Assigns week class + optional today highlight + scenario delta highlight */
  const getRowClassName = useCallback(
    (params) => {
      const classes = [];
      const d = dayjs(params.row.date);
      if (d.isValid()) {
        classes.push(d.isoWeek() % 2 === 0 ? 'week-even' : 'week-odd');
      }
      if (params.id === highlightedRowId) {
        classes.push('today-highlight');
      }
      if (selectedRow && params.id === selectedRow.id) {
        classes.push('selected-row');
      }
      if (isScenarioActive) {
        const dateKey = params.row.date
          ? String(params.row.date).split('T')[0]
          : null;
        // Row background highlights only on the earliest changed date
        if (dateKey && dateKey === earliestChangedDate) {
          const changeType = stockDeltaKeyMap[dateKey]?.changeType;
          if (changeType === 'CREATED') classes.push('scenario-created');
          else if (changeType === 'MODIFIED') classes.push('scenario-modified');
          else if (changeType === 'CANCELLED')
            classes.push('scenario-cancelled');
          else if (voyageSideEffectDates.has(dateKey))
            classes.push('scenario-voyage-side-effect');
        }
      }
      return classes.join(' ');
    },
    [
      highlightedRowId,
      selectedRow,
      stockDeltaKeyMap,
      isScenarioActive,
      voyageSideEffectDates,
      earliestChangedDate,
    ]
  );

  /** Assigns a CSS class to individual cells whose value changed in scenario mode */
  const getCellClassName = useCallback(
    (params) => {
      if (!isScenarioActive) return '';
      const dateKey = params.row.date
        ? String(params.row.date).split('T')[0]
        : null;
      if (!dateKey) return '';
      const changedFields = changedCellsMap.get(dateKey);
      if (changedFields && changedFields.has(params.field))
        return 'scenario-changed-cell';
      return '';
    },
    [isScenarioActive, changedCellsMap]
  );

  /** Scroll the grid to today's row, select and highlight it */
  const scrollToToday = () => {
    const today = dayjs().startOf('day');
    const todayRow = rowsWithId.find((r) => {
      const d = dayjs(r.date);
      return d.isValid() && d.startOf('day').isSame(today);
    });
    if (todayRow == null) return;

    setHighlightedRowId(todayRow.id);

    if (!apiRef.current) return;
    const sortedIds = apiRef.current.getSortedRowIds();
    const visualIndex = sortedIds.indexOf(todayRow.id);
    if (visualIndex !== -1) {
      // Navigate to the page containing today's row
      const pageSize = apiRef.current.state.pagination.paginationModel.pageSize;
      const targetPage = Math.floor(visualIndex / pageSize);
      apiRef.current.setPage(targetPage);

      // Scroll to the row after the page change is applied
      setTimeout(() => {
        if (!apiRef.current) return;
        const rowIndexInPage = visualIndex % pageSize;
        const rowHeight = apiRef.current.state.dimensions?.rowHeight ?? 100;
        apiRef.current.scroll({ top: rowIndexInPage * rowHeight });
      }, 0);
    }
  };

  // Auto-scroll to today when data first loads
  const hasAutoScrolled = useRef(false);
  useEffect(() => {
    if (!hasAutoScrolled.current && rowsWithId.length > 0) {
      hasAutoScrolled.current = true;
      setTimeout(() => scrollToToday(), 0);
    }
  }, [rowsWithId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply column order and width overrides from preferences
  const columns = useMemo(() => {
    const ordered = colPrefs.applyColumnOrder(stockColumnsDefinitions);
    return colPrefs.applyColumnWidths(ordered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stockColumnsDefinitions,
    colPrefs.applyColumnOrder,
    colPrefs.applyColumnWidths,
  ]);

  // Merge actions column pin into the DataGrid pinned columns
  const dataGridBaseProps = colPrefs.getColumnPrefsDataGridProps();
  const showActionsColumn =
    canQuickEditStock || (isScenarioActive && !isScenarioViewOnly);
  const mergedPinnedColumns = useMemo(() => {
    const base = dataGridBaseProps.pinnedColumns ?? {};
    if (!showActionsColumn) return base;
    const right = Array.from(new Set([...(base.right ?? []), 'actions']));
    return { ...base, right };
  }, [dataGridBaseProps.pinnedColumns, showActionsColumn]);

  const isAddVoyageDisabled = !canEditStock || isScenarioViewOnly;
  const addVoyageTooltip = isScenarioViewOnly
    ? 'Enter editing mode to add a voyage'
    : canEditStock
      ? ''
      : 'Shipping companies cannot add voyages';

  const dataGridProps = {
    ...dataGridBaseProps,
    pinnedColumns: mergedPinnedColumns,
    rows: rowsWithId,
    columns,
    rowHeight: 100,
    loading: isStockLoading,
    disableRowSelectionOnClick: true,
    getRowClassName,
    getCellClassName,
    onRowClick: (params) => setSelectedRow(params.row),
    pagination: true,
    pageSizeOptions: [50, 100, 200],
    slots: { toolbar: ColumnPrefsToolbar },
    slotProps: {
      toolbar: {
        fullScreen: isFullScreen,
        onToggleFullScreen: toggleFullScreen,
        onScrollToToday: scrollToToday,
        onSave: colPrefs.handleSaveColumns,
        ...(isFullScreen && {
          onAddVoyage: () => setIsVoyageModalOpen(true),
          addVoyageDisabled: isAddVoyageDisabled,
          addVoyageTooltip,
        }),
      },
      loadingOverlay: {
        variant: 'linear-progress',
        noRowsVariant: 'skeleton',
      },
    },
    sx: dataGridSx,
    initialState: {
      sorting: { sortModel: [{ field: 'date', sort: 'asc' }] },
    },
  };

  const sidePanel = selectedRow && (
    <Box
      sx={
        isFullScreen
          ? {
              width: 420,
              flexShrink: 0,
              ml: 2,
              overflowY: 'auto',
              height: '100%',
            }
          : {
              width: 420,
              flexShrink: 0,
              height: 'fit-content',
              position: 'sticky',
              top: 0,
            }
      }
    >
      <StockVoyageSidePanel
        key={selectedRow.id}
        row={selectedRow}
        allRows={rowsWithId}
        onClose={() => setSelectedRow(null)}
        onEditClicked={handleVoyageEdit}
      />
    </Box>
  );

  return (
    <>
      <StockQuickEditModal
        open={quickEditDate != null}
        date={quickEditDate}
        onClose={closeQuickEdit}
        onSaved={() => {
          setDeltaRefreshTick((t) => t + 1);
          onScenarioDeltaChange?.();
        }}
      />
      <GroupEditModal
        open={isVoyageModalOpen}
        onClose={() => setIsVoyageModalOpen(false)}
        mode='create'
        setSnackbarOpen={snackbar.setOpen}
        setSnackbarMessage={snackbar.setMessage}
        setSnackbarSeverity={snackbar.setSeverity}
      />
      {isFullScreen ? (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (theme) => theme.zIndex.modal,
            bgcolor: 'background.paper',
            p: 2,
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DataGridPro {...dataGridProps} />
          </Box>

          {sidePanel}
        </Box>
      ) : (
        <Stack direction='row' spacing={2} sx={{ width: '100%' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CardContainer>
              <StockModal
                isModalOpen={isStockModalOpen}
                onClose={() => {
                  setIsStockModalOpen(false);
                  onScenarioDeltaChange?.();
                }}
                onSuccess={() => setDeltaRefreshTick((t) => t + 1)}
              />
              <StockRecalculationModal
                isModalOpen={isRecalculateModalOpen}
                onClose={() => setIsRecalculateModalOpen(false)}
              />
              <CardHeader title='Stock Table' />

              <Stack
                direction='row'
                spacing={1}
                sx={{ mb: 2.5 }}
                alignItems='center'
              >
                <Tooltip
                  title={
                    isScenarioViewOnly
                      ? 'Enter editing mode to add stock'
                      : canEditStock
                        ? ''
                        : 'Shipping companies cannot modify stock'
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant='outlined'
                      color='primary'
                      onClick={() => setIsStockModalOpen(true)}
                      sx={{ minWidth: 160 }}
                      disabled={!canEditStock || isScenarioViewOnly}
                    >
                      Add Stock
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    isScenarioActive
                      ? 'Not available in scenario mode'
                      : isAdmin
                        ? ''
                        : 'Only admins can recalculate stock'
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant='outlined'
                      color='primary'
                      onClick={() => setIsRecalculateModalOpen(true)}
                      sx={{ minWidth: 160 }}
                      disabled={!isAdmin || isScenarioActive}
                    >
                      Recalculate Stock
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={addVoyageTooltip} arrow>
                  <span>
                    <Button
                      variant='outlined'
                      color='primary'
                      onClick={() => setIsVoyageModalOpen(true)}
                      sx={{ minWidth: 160 }}
                      disabled={isAddVoyageDisabled}
                    >
                      Add Voyage
                    </Button>
                  </span>
                </Tooltip>
              </Stack>

              {isScenarioActive && (
                <Stack
                  direction='row'
                  spacing={1}
                  sx={{ mb: 1.5 }}
                  alignItems='center'
                  flexWrap='wrap'
                  useFlexGap
                >
                  <Typography variant='caption' color='text.secondary'>
                    Table Reference:
                  </Typography>
                  <Chip
                    size='small'
                    label='Manual stock adjustment'
                    sx={{
                      backgroundColor: (t) =>
                        alpha(
                          t.palette.warning.main,
                          t.palette.mode === 'dark' ? 0.18 : 0.2
                        ),
                      fontSize: '0.7rem',
                      height: 22,
                    }}
                  />
                  <Chip
                    size='small'
                    label='Auto-Calculated stock adjustment'
                    sx={{
                      background: (t) => {
                        const c = alpha(
                          t.palette.warning.main,
                          t.palette.mode === 'dark' ? 0.14 : 0.25
                        );
                        return `repeating-linear-gradient(-45deg, transparent, transparent 4px, ${c} 4px, ${c} 8px)`;
                      },
                      border: '1px solid',
                      borderColor: 'divider',
                      fontSize: '0.7rem',
                      height: 22,
                    }}
                  />
                </Stack>
              )}

              <Box sx={{ height: '70vh', width: '100%' }}>
                <DataGridPro {...dataGridProps} />
              </Box>
            </CardContainer>
          </Box>

          {sidePanel}
        </Stack>
      )}
      <ExpSnackbarAlerts
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={snackbar.handleClose}
      />
      <ExpSnackbarAlerts
        open={undoSnackbar.open}
        message={undoSnackbar.message}
        severity={undoSnackbar.severity}
        onClose={() => setUndoSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default StockTable;
